import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Legal.css';

const EFFECTIVE_DATE = 'September 14, 2026';
const BUSINESS_NAME = 'Had Collective, LLC';

function LegalPage({ title, children }) {
  const navigate = useNavigate();
  return (
    <div className="legal-page">
      <div className="legal-card">
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 12 }}>
          ← Back
        </button>
        <h1>{title}</h1>
        <p className="legal-meta">{BUSINESS_NAME} · Effective {EFFECTIVE_DATE}</p>
        {children}
        <p className="legal-disclaimer">
          This page is a general policy summary and does not constitute legal advice. If you have questions
          about how your information is used, contact {BUSINESS_NAME} directly.
        </p>
      </div>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        {BUSINESS_NAME} ("we", "us") provides mediumship services and uses this booking platform to schedule
        sessions, manage client relationships, and process payments. This policy explains what information we
        collect through the platform and how we use it.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>Account details: your name, email address, and phone number.</li>
        <li>Booking information: the services you request, session dates/times, and any notes attached to a booking.</li>
        <li>Payment information: payments are processed by Stripe; we receive confirmation that a payment succeeded and an amount, but your card details are never stored on our servers.</li>
        <li>Session recordings: only when you book a service marked as recordable and you explicitly opt in at booking time — see "Session recordings" below.</li>
        <li>Communications: messages, session notes, and files you or your practitioner exchange through the platform.</li>
      </ul>

      <h2>How we use your information</h2>
      <p>
        We use your information solely to provide our services and manage our relationship with you — scheduling
        and confirming bookings, processing payments and invoices, keeping a record of your session history, and
        contacting you about your bookings. We do not sell your information, and we do not use it for advertising
        or share it with third parties for their own marketing purposes.
      </p>

      <h2>Who we share it with</h2>
      <p>
        We only share information with service providers necessary to run the platform: Stripe for payment
        processing, and Google (Calendar/Meet) when a practitioner uses those integrations to schedule or host a
        session. These providers are only given what they need to perform that function.
      </p>

      <h2>Session recordings</h2>
      <p>
        Some services may be offered as recordable. If you request that your session be recorded, you'll be asked
        to consent at the time of booking. A recording is only made when you've opted in, and it is made available
        to you and your practitioner. Recording is never turned on without your consent.
      </p>

      <h2>Contact from us</h2>
      <p>
        We'll contact you about things related to your bookings — confirmations, reminders, invoices, and
        session-related messages from your practitioner. We won't use your contact information for unrelated
        marketing.
      </p>

      <h2>Your choices</h2>
      <p>
        You can ask us to see what information we hold about you, correct it, or request that your account be
        deactivated and your data deleted, subject to what we're required to keep for financial recordkeeping.
        Contact {BUSINESS_NAME} to make a request.
      </p>
    </LegalPage>
  );
}

export function TermsAndConditions() {
  return (
    <LegalPage title="Terms & Conditions">
      <p>
        These terms govern your use of the {BUSINESS_NAME} booking platform to request, pay for, and attend
        mediumship sessions. By creating an account, you agree to these terms.
      </p>

      <h2>Bookings and payment</h2>
      <p>
        Requesting a session is not a confirmed booking until your practitioner approves it and you complete
        payment. Prices, durations, and availability are set by each practitioner and may change. Cancellation
        and rescheduling are handled through the platform; contact your practitioner directly for anything not
        covered there.
      </p>

      <h2>Accounts</h2>
      <p>
        You're responsible for keeping your login credentials secure and for the accuracy of the information you
        provide. Practitioner accounts are created or upgraded by {BUSINESS_NAME} administrators only — you can't
        register directly as a practitioner.
      </p>

      <h2>Session recordings</h2>
      <p>
        Some services can be recorded, but only if you explicitly opt in when booking. By checking the recording
        consent box, you agree to your session being recorded and to the recording being made available to you and
        your practitioner for purposes related to your services. Recording another participant without their
        knowledge is not permitted.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Use the platform only for its intended purpose of booking and managing mediumship sessions. Don't
        misrepresent your identity, interfere with the platform's operation, or use it to harass another user.
      </p>

      <h2>Changes</h2>
      <p>
        We may update these terms or our Privacy Policy from time to time. Continued use of the platform after a
        change means you accept the updated terms.
      </p>
    </LegalPage>
  );
}
